import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { userEvolutionQuery, type Bucket, type OwnMedian, type PeerGroup, type Totals } from '../api/productivitee'
import type { Period } from '../api/securitee'
import { durationString } from '../easeedesk/format'
import { Spinner } from '../ui/Spinner'
import { SettingsRadio } from '../ui/SettingsParts'
import { PeriodWithPresets } from './period'
import { Delta, METRIC_LABEL, SHARE_HINT, TrendChart, TrendLegend, metricText, type Metric } from './EvolutionChart'

/** Cum se cheama, in vorbe, de unde vine grupul cu care se compara omul. */
const GROUP_SOURCE: Record<PeerGroup['source'], string> = {
  department: 'department, from the company structure',
  group: "device group - the person has no department, so this is the group of the machines they worked on",
  none: '',
}

/**
 * Fila EVOLUTION din randul desfacut al listei User Productivity.
 *
 * -----------------------------------------------------------------------------
 * DE CE O FILA, SI NU UN PANOU IN SUMMARY
 * -----------------------------------------------------------------------------
 *
 * Summary e despre O ZI: patru cifre ale zilei si banda de 24 de ore a fiecarei
 * masini. Un desen pe trei luni pus sub ele ar fi facut fila sa raspunda la
 * doua intrebari deodata, si nimic pe ecran n-ar fi spus la care se uita omul -
 * "Active time 6h 20m" de sus ar fi ramas al zilei, iar coloana de dedesubt ar
 * fi fost a lunii. Mai rau, fila zilei n-are selector de perioada, si i-ar fi
 * trebuit unul care sa nu atinga ziua din antet.
 *
 * Fila proprie isi are perioada ei, cu scurtaturile si `Custom range` ale
 * modulului (`period.tsx`), si nu schimba nimic din ce era.
 *
 * -----------------------------------------------------------------------------
 * CE ARATA, SI DE UNDE
 * -----------------------------------------------------------------------------
 *
 * `it_productivitee_user_summary`, un rand pe om pe zi, de luni intregi - de
 * cand exista, modulul se uita numai la ziua de azi. Timpul de lucru, cel activ
 * si procentul dintre ele; pe zile pana la o luna, pe saptamani pana la o
 * jumatate de an, pe luni peste. Langa fiecare treapta, treapta perioadei
 * dinaintea ei, de aceeasi lungime.
 *
 * NU se arata "Productivity": functia stocata care o socoteste raspunde 0.00%
 * si in portalul vechi, si in v4 (e chemata cu o data acolo unde asteapta un
 * numar). O linie dreapta pe zero desenata frumos ar fi fost mai rea decat
 * lipsa ei. Cifra de aici e TIMPUL ACTIV din timpul de lucru - amandoua scrise
 * de agent, in acelasi rand.
 *
 * -----------------------------------------------------------------------------
 * FATA DE GRUPUL LUI, SI FATA DE EL INSUSI
 * -----------------------------------------------------------------------------
 *
 * Peste coloane trec doua linii:
 *
 *   - MEDIANA GRUPULUI, treapta cu treapta (linia mov). Mediana, nu media: un
 *     om plecat in concediu si unul care sta douasprezece ore la masina trag
 *     media in partile lor si strica comparatia pentru toti ceilalti. Se rupe
 *     acolo unde nimeni n-a raportat, si nu se deseneaza deloc sub trei oameni
 *     cu date in perioada - cu doi, "mediana grupului" e chiar celalalt om;
 *   - MEDIANA LUI din perioada dinainte (linia verde intrerupta). De multe ori
 *     asta e cifra care conteaza: sub grup, dar in urcare de trei saptamani.
 *
 * Ecranul scrie de fiecare data CU CE grup compara si de unde stie grupul:
 * departamentul persoanei din structura firmei, sau - cand structura nu stie
 * de el - grupul masinii pe care a stat cele mai multe zile. Cele doua nu
 * inseamna acelasi lucru, si un om care lucreaza pe doua masini din doua
 * grupuri si-ar schimba "grupul" fara sa faca nimic.
 *
 * Nicaieri nu se scrie numele altui om, si nu exista niciun clasament: o
 * mediana nu are nume.
 */

const METRICS: Metric[] = ['share', 'active_time', 'working_time']

/** Ziua cu `n` zile inainte, in aceeasi scriere `Y-m-d`. */
export function daysBefore(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00`)
  if (Number.isNaN(d.getTime())) return day
  d.setDate(d.getDate() - n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Ultimele 30 de zile care se termina in ziua randului - fereastra cu care se deschide fila. */
export function defaultPeriod(day: string): Period {
  return { from: daysBefore(day, 29), to: day }
}

/**
 * O dala cu cifra perioadei si, sub ea, cat s-a schimbat fata de perioada
 * dinainte. Diferenta de TIMP se scrie ca durata ("+1h 20m 00s"), nu in
 * secunde goale: "+4800s" nu se citeste.
 *
 * NU e `dashboard-stat` al portalului vechi: skinul ala e o caramida albastra
 * cu eticheta in bara de jos, si pe ea verdele si rosul sageetii de "mai bine
 * / mai rau" nu se mai vad - masurat in Edge, pe foaia adevarata. Dala de aici
 * e alba si linistita, cu eticheta deasupra cifrei, ca pe ecranele desenului
 * nou.
 */
function Tile({ label, value, delta, duration, note }: { label: string; value: string; delta?: number; duration?: boolean; note?: string }) {
  return (
    <div className="col-md-3">
      <div style={{ background: '#fff', border: '1px solid #e5e8eb', borderRadius: 4, padding: '10px 12px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#8a9199' }}>{label}</div>
        <div style={{ fontSize: 24, lineHeight: '30px', color: '#33414e' }}>{value}</div>
        {delta === undefined ? null : (
          <div style={{ fontSize: 12 }}>
            <Delta value={delta} format={duration ? (size) => durationString(Math.round(size)) : undefined} />{' '}
            <span style={{ color: '#8a9199' }}>vs previous</span>
          </div>
        )}
        {/* SUMA perioadei, si peste cate zile e. Fara numarul de zile langa ea,
            suma si media de deasupra ei se contrazic pe ecran: saizeci de ore
            pe o luna pare putin langa "5h pe zi", pana cand omul afla ca au
            fost douasprezece zile lucrate, nu douazeci. */}
        {note === undefined ? null : <div style={{ fontSize: 11, color: '#8a9199', marginTop: 2 }}>{note}</div>}
      </div>
    </div>
  )
}

/** Care cifra de medie din `Totals` merge cu care metrica de timp. */
const AVERAGE_FIELD = { working_time: 'avg_working_time', active_time: 'avg_active_time' } as const

/**
 * MEDIA PE ZI CU RAPORT a unei metrici, asa cum a socotit-o serverul
 * (`Productivitee::withAverages`) - nu se mai imparte nimic aici, ca sa nu
 * existe doua socoteli ale aceluiasi lucru.
 *
 * Procentul e deja un raport, deci media lui e el insusi: `share` nu se atinge.
 * `null` inseamna "nicio zi cu raport", nu zero.
 */
export function averageOf(totals: Totals, metric: Metric): number | null {
  return metric === 'share' ? totals.share : totals[AVERAGE_FIELD[metric]]
}

/**
 * Cat s-a schimbat fata de perioada dinainte: MEDIE fata de MEDIE.
 *
 * Pe SUME comparatia minte cand cele doua perioade n-au acelasi numar de zile
 * cu raport - si n-au aproape niciodata: 1-30 septembrie cu douazeci de zile
 * lucrate fata de 2-31 august cu optsprezece iesea "+11%" fara ca nimeni sa
 * fi lucrat altfel, doar fiindca in prima erau doua zile mai mult. Pe medii
 * diferenta e a omului, nu a calendarului.
 *
 * `undefined` cand una din perioade n-are nicio zi cu raport: atunci nu exista
 * ce compara, si sageata nu se deseneaza deloc. Zero ar fi spus "neschimbat".
 */
export function deltaOf(now: Totals, before: Totals, metric: Metric): number | undefined {
  if (metric === 'share') return Math.round((now.share - before.share) * 10) / 10
  const a = averageOf(now, metric)
  const b = averageOf(before, metric)
  return a === null || b === null ? undefined : a - b
}

/**
 * Treapta cea mai plina si cea mai slaba din perioada.
 *
 * Roxana, 25 septembrie 2026, la "ce ar mai fi util": doi oameni cu aceeasi
 * medie de opt ore pot fi foarte diferiti - unul face opt ore in fiecare zi,
 * celalalt patrusprezece si doua alternativ. Media ii arata la fel, si exact
 * asta cauta omul care se uita la ecran.
 *
 * Intra numai treptele CU RAPORT: o zi tacuta are zero peste tot si ar fi iesit
 * "ziua cea mai slaba" de fiecare data - aceeasi capcana ca la mediana, si
 * acelasi leac. Cand nu raman doua trepte, sau cand toate au aceeasi cifra, nu
 * se scrie nimic: nu e nicio intindere de aratat.
 */
export function spreadOf(buckets: Bucket[], metric: Metric): { high: Bucket; low: Bucket } | undefined {
  const reported = buckets.filter((b) => b.days > 0)
  if (reported.length < 2) return undefined
  const value = (b: Bucket): number => (metric === 'share' ? b.share : b[metric])
  let high = reported[0]
  let low = reported[0]
  reported.forEach((b) => {
    if (value(b) > value(high)) high = b
    if (value(b) < value(low)) low = b
  })

  return value(high) === value(low) ? undefined : { high, low }
}

/** Procentul unei parti din intreg, cu numitorul scris langa: "12 / 30". */
function Coverage({ label, part, whole }: { label: string; part: number; whole: number }) {
  const share = whole <= 0 ? 0 : Math.round((part / whole) * 100)
  return (
    <span>
      {label}: <strong>{part}</strong> / {whole} <span style={{ color: '#888' }}>({share}%)</span>
    </span>
  )
}

export function UserEvolutionTab({ id, day, period: initial }: { id: number; day: string; period?: Period }) {
  const [period, setPeriod] = useState<Period>(() => initial ?? defaultPeriod(day))
  const [metric, setMetric] = useState<Metric>('share')
  const q = useQuery(userEvolutionQuery(id, period.from, period.to))
  const d = q.data

  /* Media pe zi cu raport, si suma perioadei care sta sub ea - amandoua purtand
     numarul de zile pe fata. `spreadOf` raspunde la ce ascunde o medie. */
  const average = (m: Metric): string => {
    if (!d) return '-'
    const value = averageOf(d.totals, m)
    return value === null ? '-' : m === 'share' ? `${value}%` : durationString(value)
  }
  const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'} with a summary`
  const span = (seconds: number): string =>
    /* `durationString` lasa un spatiu la coada ("16h "), si doua spatii inaintea
       lui "over" se vad in text chiar daca browserul le strange la unul. */
    !d || d.reported_days === 0 ? 'No day of this period has a summary' : `${durationString(seconds).trim()} over ${days(d.reported_days)}`
  const spread = d ? spreadOf(d.buckets, metric) : undefined

  /* Linia grupului si reperul propriu, pe metrica aleasa acum. */
  const median = d && d.group.comparable ? d.group.median.map((m) => m[metric]) : undefined
  const own = (m: OwnMedian): number | null => m[metric]
  const reference = d && own(d.own_median_previous) !== null
    ? { value: own(d.own_median_previous) as number, label: `your median before: ${metricText(metric, own(d.own_median_previous) as number)}` }
    : undefined

  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <div className="col-md-12" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <PeriodWithPresets period={period} onChange={setPeriod} />
          <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
            {METRICS.map((m) => (
              <SettingsRadio
                key={m}
                name={`evolution-metric-${id}`}
                value={m}
                checked={metric === m}
                label={METRIC_LABEL[m]}
                onChange={(on) => { if (on) setMetric(m) }}
              />
            ))}
          </span>
        </div>
      </div>

      {q.error ? <div className="alert alert-danger">Something went wrong: {q.error.message}</div> : null}
      {!d && !q.error ? <Spinner /> : null}

      {d ? (
        <>
          <div className="row">
            {/*
              MEDIA e cifra mare, SUMA sta sub ea, si numarul de zile e scris
              langa amandoua - Roxana, 25 septembrie 2026: "e average
              intotdeauna", si apoi "ar merge si average si in plus pe o
              perioada si suma".
            */}
            <Tile
              label="Working time / day"
              value={average('working_time')}
              delta={deltaOf(d.totals, d.previous_totals, 'working_time')}
              duration
              note={span(d.totals.working_time)}
            />
            <Tile
              label="Active time / day"
              value={average('active_time')}
              delta={deltaOf(d.totals, d.previous_totals, 'active_time')}
              duration
              note={span(d.totals.active_time)}
            />
            {/* Procentul e deja un raport: n-are nici medie, nici suma. */}
            <Tile label={METRIC_LABEL.share} value={`${d.totals.share}%`} delta={deltaOf(d.totals, d.previous_totals, 'share')} />
            <Tile label="Days with a summary" value={`${d.reported_days} / ${d.period_days}`} note="the denominator of both averages" />
          </div>

          {/* Ce e procentul, intr-un rand, sub dalele in care apare prima oara. */}
          <div className="row" style={{ marginTop: -4, marginBottom: 10 }}>
            <div className="col-md-12" style={{ fontSize: 12, color: '#666' }}>{SHARE_HINT}</div>
          </div>

          <div className="row">
            <div className="col-md-12 divider">
              <h4 className="bordered">{METRIC_LABEL[metric]} by {d.grain}</h4>
            </div>
            <div className="col-md-12">
              {d.buckets.length === 0 ? (
                <p className="color-light-grey">No day of this period has a summary for this user.</p>
              ) : (
                <>
                  <TrendChart buckets={d.buckets} previous={d.previous.buckets} metric={metric} median={median} reference={reference} />
                  <TrendLegend
                    now={`${d.from} - ${d.to}`}
                    before={`${d.previous.from} - ${d.previous.to}`}
                    median={median ? `${d.group.name || 'group'} median (${d.group.people} people)` : undefined}
                    reference={reference ? 'your median in the previous period' : undefined}
                  />
                </>
              )}
            </div>
          </div>

          {/*
            CU CE se compara, spus pe fata. Cand nu se compara, se spune si
            asta - linistit, intr-un rand, in loc sa lipseasca linia fara
            niciun cuvant si omul sa creada ca ecranul e stricat.
          */}
          <div className="row">
            <div className="col-md-12" style={{ fontSize: 12, color: '#666' }}>
              {d.group.source === 'none' ? (
                <span>No group to compare against: this person has no department in the company structure and no machine of a group in this period.</span>
              ) : d.group.comparable ? (
                <span>
                  Compared against the median of <strong>{d.group.name || 'their group'}</strong> ({GROUP_SOURCE[d.group.source]}), {d.group.people} people with
                  data in this period. Days nobody worked are left out of the median, so a holiday does not drag anyone down.
                </span>
              ) : (
                <span>
                  Too few people in <strong>{d.group.name || 'this group'}</strong> to compare - {d.group.people} with data in this period, and the median needs
                  more than two.
                </span>
              )}
              {/*
                SI CEALALTA LINIE. Graficul are doua repere - mediana grupului
                (mov) si mediana LUI din perioada dinainte (verde intrerupt) - si
                pana acum numai lipsa celei mov era spusa in vorbe. Cand omul n-a
                raportat nicio zi in perioada dinainte, reperul verde nu se poate
                desena, si dispare in tacere: pe ecran arata ca ecranul a pierdut
                o linie, nu ca n-are din ce sa o traga. Roxana, 25 septembrie
                2026: "la grafic sa aibe 2, in raportul cu departamentul si in
                raport cu istoricul lui".
              */}
              {d.buckets.length > 0 && !reference ? (
                <span>
                  {' '}
                  No line for your own median before: no day of {d.previous.from} - {d.previous.to} has a summary for this user.
                </span>
              ) : null}
            </div>
          </div>

          {/*
            Cate zile din perioada au raport. Fara randul asta, o luna cu patru
            zile masurate se deseneaza la fel de increzator ca una cu treizeci,
            si "a scazut" ar putea sa insemne doar ca agentul a tacut.
          */}
          <div className="row">
            <div className="col-md-12" style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              <Coverage label="Days with a summary" part={d.reported_days} whole={d.period_days} />
              {/*
                CE ASCUNDE O MEDIE. Treapta cea mai plina si cea mai slaba,
                dintre cele cu raport - "Total this period" care sta aici pana
                acum s-a mutat pe dalele de sus, langa media din care iese, si
                scris de doua ori in doua feluri nu ajuta nimanui.
              */}
              {spread ? (
                <>
                  {' · '}
                  <span>
                    Highest {d.grain}: {spread.high.label} ({metricText(metric, metric === 'share' ? spread.high.share : spread.high[metric])})
                  </span>
                  {' · '}
                  <span>
                    Lowest {d.grain}: {spread.low.label} ({metricText(metric, metric === 'share' ? spread.low.share : spread.low[metric])})
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}
